<template>
    <div class="carrier-profile-wrapper">
      <b-loading :active="isFetching" />

      <h2 class="title">
        {{ details.accountFirstName }} {{ details.accountLastName }}
      </h2>
      <h3 class="subtitle">Driver Licenses Number: {{ details.carrierCommercialDriverLicenseNumber }}</h3>


      <!--
          Only show the cards once we are done loading the data.
           -->
      <template v-if="!isFetching">
        <div class="columns is-multiline">
          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  Carrier Information
                </p>
              </div>
              <div class="card-content content">
                <dl class="floated-list">
                  <dt>First Name</dt>
                  <dd>{{ details.accountFirstName }}</dd>

                  <dt>Last Name</dt>
                  <dd>{{ details.accountLastName }}</dd>

                  <dt>Date of Birth</dt>
                  <dd>{{ birthDate }}</dd>

                  <dt>Email Address</dt>
                  <dd>
                    <a :href="`mailto:${details.accountEmailAddress}`">{{ details.accountEmailAddress }}</a>
                  </dd>

                  <dt>Phone Number</dt>
                  <dd>{{ phoneNumber }}</dd>

                  <template v-for="doc in documentList">
                  <dt :key="doc.fileName">Uploaded {{ doc.displayName }}</dt>
                  <dd :key="doc.fileName">
                    <font-awesome-icon v-if="details[doc.dbFieldName]" icon="check" />
                    <font-awesome-icon v-else icon="times" />
                  </dd>
                  </template>

                </dl>
                <div class="is-clearfix"></div>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  Licenses Information
                </p>
              </div>
              <div class="card-content content">
                <dl class="floated-list">
                  <dt>Driver Licenses Number</dt>
                  <dd>{{ details.carrierCommercialDriverLicenseNumber }}</dd>

                  <dt>MC/DOT Number</dt>
                  <dd>{{ details.carrierMotorCarrierNumber }}</dd>

                </dl>
                <div class="is-clearfix"></div>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  Insurance
                </p>
              </div>
              <div class="card-content content">
                <dl class="floated-list">
                  <dt>Issuer Name</dt>
                  <dd>{{ details.carrierInsurancePolicyCompanyName }}</dd>

                  <dt>Policy Number</dt>
                  <dd>{{ details.carrierInsurancePolicyNumber }}</dd>

                  <dt>Effective Date</dt>
                  <dd>{{ insuranceDate }}</dd>

                  <dt>Currently Active</dt>
                  <dd>{{ details.carrierInsurancePolicyActiveFlag ? 'Yes' : 'No' }}</dd>
                </dl>
                <div class="is-clearfix"></div>
              </div>
            </div>
          </div>
          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  Equipment
                </p>
              </div>
              <div class="card-content content">
                <dl class="floated-list">
                  <dt>Make & Model</dt>
                  <dd>{{ details.carrierEquipmentTruckManufacturer }}</dd>

                  <dt>Build Year</dt>
                  <dd>{{ details.carrierEquipmentTruckYear }}</dd>

                  <dt>Trailer Type</dt>
                  <dd>{{ details.carrierEquipmentTrailerDescription1 }}</dd>

                  <dt>Additional Trailer Type</dt>
                  <dd>{{ details.carrierEquipmentTrailerDescription2 }}</dd>
                </dl>
                <div class="is-clearfix"></div>
              </div>
            </div>
          </div>

          <div class="column is-full">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  Carrier Documents
                </p>
              </div>
              <div class="card-content content">
                <document-library :account-key="accountKey" :can-delete="false"/>
                <div class="is-clearfix"></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

</template>

<script>
/**
 * First Name
 * Last Name
 * Driver Licenses No.
 * Birth Date
 * MC/DOT Number
 * Phone Number
 * Email
 *
 * Equipment
 *  Truck make & model
 *  build year
 *  trailer type
 *  another trailer type
 *
 *  Insurance
 *  Issuer Name
 *  Policy Number
 *  Effice Date
 *  Active/Inactive
 *
 *
 */

import AccountApi from '@/api-interface/user-account'
import { format } from 'date-fns'
import DocumentLibrary from '@/components/CompanyDocumentsTable/index'
import CarrierFileUploads from '@/lookup-data/carrier-file-uploads'

const DATE_FORMAT = 'MMM DD, YYYY'
export default {
  name: 'CarrierProfile',

  data() {
    return {
      documentList: CarrierFileUploads,
      accountKey: null,
      isFetching: false,
      showError: false,
      rawDetails: {
        carrier: {}
      }
    }
  },

  async created() {
    this.accountKey = this.$route.params.accountKey
    this.isFetching = true
    try {
      this.rawDetails = await AccountApi.getAccountByKey(this.accountKey)
    } catch (error) {
      console.error(`Error fetching account details ${this.accountKey} `, error)
      this.showError = true
    } finally {
      this.isFetching = false
    }
  },

  computed: {
    details() {
      const rawDetails = this.rawDetails
      return {
        ...rawDetails,
        ...rawDetails.carrier
      }
    },

    birthDate() {
      return format(this.rawDetails.carrier.carrierDateOfBirth, DATE_FORMAT)
    },

    insuranceDate() {
      return format(this.rawDetails.carrier.carrierInsurancePolicyEffectiveTimestamp, DATE_FORMAT)
    },

    phoneNumber() {
      let phone = this.rawDetails.accountPhones[0].accountPhoneDigits

      if (phone) {
        phone = String(phone)
        const first = phone.substring(0, 3)
        const second = phone.substring(3, 6)
        const third = phone.substring(6)

        return `${first}-${second}-${third}`
      } else {
        return ''
      }
    }
  },

  components: {
    DocumentLibrary
  }
}
</script>

<style scoped lang="scss">
.carrier-profile-wrapper {
  min-height: 85vh;
  &::after {
    // TODO: this would be great as a mix-in
    content: '';
    background-image: url('../assets/backgrounds/shutterstock_1040943541.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0.4;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: fixed;
    z-index: -1;
  }
}

.card {
  min-height: 200px;
}
</style>
